import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Heart,
  ImageIcon,
  Loader2,
  MessageCircle,
  Minus,
  Pencil,
  PlayCircle,
  Plus,
  Send,
  ShoppingBag,
  Star,
  UtensilsCrossed,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { publicMenuApi } from '../../api';
import { useLanguage } from '../../contexts/LanguageContext';
import resolveMediaUrl from '../../utils/resolveMediaUrl';
import '../../styles/PublicMenu/public-product-details.css';
const PUBLIC_MENU_CART_PREFIX = 'public_menu_cart_';
const PUBLIC_MENU_LIKES_PREFIX = 'public_menu_likes_';
const DEFAULT_PUBLIC_MENU_THEME = {
  theme: 'light',
  primary_color: '#51766E',
  background_color: '#F8FAF9',
  text_color: '#333333',
  surface_color: '#FFFFFF',
  accent_color: '#D7E3E0',
  border_radius: 'large',
  card_style: 'elevated',
  card_shadow: 'soft',
  font_family: 'system',
};

const hexToRgb = (color, fallback) => {
  if (!color) return fallback;
  const value = String(color).trim();
  if (value.startsWith('rgb')) {
    const parts = value.match(/\d+(?:\\.\d+)?/g);
    if (parts?.length >= 3) return parts.slice(0, 3).join(', ');
  }

  const normalized = value.replace('#', '');
  const fullHex =
    normalized.length === 3
      ? normalized.split('').map((part) => `${part}${part}`).join('')
      : normalized;
  if (!/^[0-9a-fA-F]{6}$/.test(fullHex)) return fallback;
  return `${parseInt(fullHex.slice(0, 2), 16)}, ${parseInt(fullHex.slice(2, 4), 16)}, ${parseInt(fullHex.slice(4, 6), 16)}`;
};

const buildPublicProductTheme = (menuData) => {
  const menu = menuData?.menu || {};
  const establishment = menuData?.establishment || {};
  const template = menuData?.template || menu?.template || menu?.menu_template || {};
  return {
    ...DEFAULT_PUBLIC_MENU_THEME,
    ...(template?.config || {}),
    ...(establishment?.menu_design_settings || {}),
    ...(menuData?.design_settings || {}),
    ...(menu?.design_settings || {}),
    ...(menu?.menu_design_settings || {}),
  };

};

const UNAVAILABLE_STATUSES = ['hidden', 'inactive', 'sold_out', 'unavailable'];
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogg', 'mov', 'm4v'];
const isEnabledFlag = (value, defaultValue = false) => {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  return ['1', 'true', 'yes', 'on'].includes(String(value).toLowerCase());
};

const isVideoMedia = (media) => {
  const explicitType = String(media?.type || media?.media_type || media?.mime_type || '')
    .toLowerCase();
  if (explicitType.includes('video')) return true;
  const source = String(media?.url || media?.path || media?.file || media || '')
    .split('?')[0]
    .toLowerCase();
  const extension = source.split('.').pop();
  return VIDEO_EXTENSIONS.includes(extension);
};

const readCartItems = (storageKey) => {
  try {
    return JSON.parse(localStorage.getItem(storageKey) || '[]');
  } catch {
    return [];

  }

};

const saveCartItems = (storageKey, items) => {
  localStorage.setItem(storageKey, JSON.stringify(items));
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (value && typeof value === 'object') return Object.values(value);
  return [];

};

const normalizeTranslations = (translations) => {
  if (Array.isArray(translations)) return translations.filter(Boolean);
  if (translations && typeof translations === 'object') {
    return Object.entries(translations).map(([locale, translation]) => {
      if (!translation || typeof translation !== 'object') {
        return { locale, name: translation ? String(translation) : '' };
      }

      return {
        locale: translation.locale || locale,
        ...translation,
      };

    });
  }

  return [];

};

const getTranslationValue = (item, field, language, fallback = 'fr') => {
  if (!item) return '';
  if (item?.translations?.[language]?.[field]) {
    return item.translations[language][field];
  }

  if (item?.translations?.[fallback]?.[field]) {
    return item.translations[fallback][field];
  }

  const translations = normalizeTranslations(item?.translations);
  const selectedTranslation =
    translations.find((translation) => translation.locale === language) ||
    translations.find((translation) => translation.locale === fallback) ||
    translations.find((translation) => translation.locale === 'en') ||
    translations.find((translation) => translation.locale === 'ar') ||
    translations.find((translation) => translation.locale === 'es') ||
    translations[0];
  return selectedTranslation?.[field] || item?.[field] || '';
};

const formatPrice = (value, currency = 'MAD') => {
  if (value === null || value === undefined || value === '') return null;
  return `${Number(value || 0).toFixed(2)} ${currency}`;
};

const getProductMedia = (product) => {
  const mediaItems = [
    ...toArray(product?.media),
    ...toArray(product?.images),
    ...toArray(product?.videos),
    product?.image ? { url: product.image } : null,
    product?.image_url ? { url: product.image_url } : null,
    product?.video ? { url: product.video, type: 'video' } : null,
    product?.video_url ? { url: product.video_url, type: 'video' } : null,
  ].filter(Boolean);
  const uniqueMedia = new Map();
  mediaItems
    .slice()
    .sort((firstMedia, secondMedia) => {
      return Number(secondMedia?.is_primary || 0) - Number(firstMedia?.is_primary || 0);
    })
    .forEach((media) => {
      const rawUrl =
        media?.url ||
        media?.path ||
        media?.file ||
        media?.video_url ||
        media?.image_url ||
        media?.src ||
        media;
      const url = resolveMediaUrl(rawUrl);
      if (!url) return;
      uniqueMedia.set(url, {
        id: media?.id || url,
        url,
        thumbnail: resolveMediaUrl(
          media?.thumbnail || media?.thumbnail_url || media?.poster,
        ),
        type: isVideoMedia(media) ? 'video' : 'image',
        is_primary: media?.is_primary,
      });
    });
  return Array.from(uniqueMedia.values());
};

const getProductSizes = (product) => toArray(product?.sizes);
const getProductSupplements = (product) => toArray(product?.supplements);
const getProductAllergens = (product) => toArray(product?.allergens);
const getProductDietaryLabels = (product) => toArray(product?.dietary_labels);
const getEngagement = (product) => product?.engagement || {};
const getReviews = (product) => {
  return toArray(product?.reviews || product?.latest_reviews || product?.comments);
};

const getProductCurrency = (product, establishment) => {
  return product?.currency || establishment?.currency || 'MAD';
};

const getProductBasePrice = (product, sizeId = '') => {
  const sizes = getProductSizes(product);
  if (sizeId && sizes.length > 0) {
    const selectedSize = sizes.find(
      (size) => Number(size.id) === Number(sizeId),
    );

    if (selectedSize) {
      return Number(selectedSize.discount_price ?? selectedSize.price ?? 0);
    }

  }

  return Number(
    product?.discount_price ||
      product?.base_price ||
      product?.price ||
      0,
  );

};

const getSupplementPrice = (supplement) => {
  return Number(
    supplement?.price_override ||
      supplement?.price ||
      supplement?.extra_price ||
      0,
  );

};

const getProductPrice = (product, t, establishment) => {
  const currency = getProductCurrency(product, establishment);
  const sizes = getProductSizes(product).filter((size) => size.is_active !== false);
  if (product?.pricing_type === 'sizes' && sizes.length > 0) {
    const prices = sizes
      .map((size) => Number(size.discount_price ?? size.price))
      .filter((price) => !Number.isNaN(price));
    if (!prices.length) {
      return t('products.pricing.noPrice');
    }

    return `${t('products.pricing.from')} ${Math.min(...prices).toFixed(2)} ${currency}`;
  }

  if (product?.discount_price != null) {
    return formatPrice(product.discount_price, currency);
  }

  return (
    formatPrice(product?.base_price ?? product?.price, currency) ||
    t('products.pricing.noPrice')
  );

};

const isProductAvailable = (product) => {
  return !UNAVAILABLE_STATUSES.includes(
    String(product?.status || '').toLowerCase(),
  );

};

const getAllProductsFromMenu = (menuData) => {
  const allProducts = [
    ...toArray(menuData?.products),
    ...toArray(menuData?.featured_products),
  ];

  toArray(menuData?.categories).forEach((category) => {
    allProducts.push(...toArray(category.products));
    toArray(category.children).forEach((childCategory) => {
      allProducts.push(...toArray(childCategory.products));
    });
  });
  const uniqueProducts = new Map();
  allProducts.filter(Boolean).forEach((product) => {
    uniqueProducts.set(String(product.id), product);
  });
  return Array.from(uniqueProducts.values());
};

const findProductCategory = (menuData, product) => {
  if (!product) return null;
  let foundCategory = null;
  toArray(menuData?.categories).forEach((category) => {
    if (Number(product.category_id) === Number(category.id)) {
      foundCategory = category;
    }

    toArray(category.children).forEach((childCategory) => {
      if (Number(product.category_id) === Number(childCategory.id)) {
        foundCategory = childCategory;
      }

    });
  });
  return foundCategory;
};

function ProductDetailsCustomAlert({ alertMessage, onClose, closeLabel }) {
  if (!alertMessage) return null;
  const isSuccess = alertMessage.type === 'success';
  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 18,
        right: 18,
        zIndex: 250,
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr) auto',
        gap: 10,
        alignItems: 'center',
        width: 'min(420px, calc(100vw - 32px))',
        padding: '14px 14px',
        color: isSuccess ? '#065f46' : '#991b1b',
        border: `1px solid ${isSuccess ? 'rgba(16, 185, 129, 0.22)' : 'rgba(239, 68, 68, 0.22)'}`,
        borderRadius: 22,
        background: isSuccess
          ? 'linear-gradient(135deg, rgba(236, 253, 245, 0.98), rgba(255, 255, 255, 0.96))'
          : 'linear-gradient(135deg, rgba(254, 242, 242, 0.98), rgba(255, 255, 255, 0.96))',
        boxShadow: '0 22px 70px rgba(15, 23, 42, 0.18)',
        backdropFilter: 'blur(14px)',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          width: 36,
          height: 36,
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff',
          borderRadius: 14,
          background: isSuccess
            ? 'linear-gradient(135deg, #10b981, #047857)'
            : 'linear-gradient(135deg, #ef4444, #b91c1c)',
        }}
      >
        {isSuccess ? <CheckCircle2 size={19} /> : <AlertTriangle size={19} />}
      </span>
      <span
        style={{
          minWidth: 0,
          color: isSuccess ? '#065f46' : '#991b1b',
          fontSize: 13,
          fontWeight: 900,
          lineHeight: 1.45,
        }}
      >
        {alertMessage.text}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        {alertMessage.actionTo && (
          <Link
            to={alertMessage.actionTo}
            style={{
              display: 'inline-flex',
              minHeight: 34,
              alignItems: 'center',
              padding: '0 11px',
              color: '#ffffff',
              borderRadius: 999,
              background: isSuccess ? '#047857' : '#b91c1c',
              fontSize: 12,
              fontWeight: 950,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {alertMessage.actionLabel}
          </Link>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          style={{
            display: 'inline-flex',
            width: 30,
            height: 30,
            alignItems: 'center',
            justifyContent: 'center',
            color: isSuccess ? '#065f46' : '#991b1b',
            border: 0,
            borderRadius: 999,
            background: isSuccess ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
            fontSize: 18,
            fontWeight: 950,
            cursor: 'pointer',
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );

}

const triggerErrorShake = () => {
  document.body.classList.remove('public-product-error-shake');
  window.requestAnimationFrame(() => {
    document.body.classList.add('public-product-error-shake');
    window.setTimeout(() => {
      document.body.classList.remove('public-product-error-shake');
    }, 480);
  });
};

function PublicProductDetails() {
  const { establishmentSlug, menuSlug, productId } = useParams();
  const menuStorageId = `${establishmentSlug || ''}__${menuSlug || ''}`;
  const publicMenuPath = `/${encodeURIComponent(establishmentSlug || '')}/${encodeURIComponent(menuSlug || '')}`;
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [menuData, setMenuData] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [hiddenMediaUrls, setHiddenMediaUrls] = useState([]);
  const [selectedSizeId, setSelectedSizeId] = useState('');
  const [selectedSupplements, setSelectedSupplements] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [itemNote, setItemNote] = useState('');
  const [likedProductIds, setLikedProductIds] = useState([]);
  const [localReviews, setLocalReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({
    customer_name: '',
    rating: 5,
    comment: '',
  });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const establishment = menuData?.establishment || null;
  const menu = menuData?.menu || null;
  const productTheme = useMemo(() => buildPublicProductTheme(menuData), [menuData]);
  const productThemeStyle = useMemo(
    () => ({
      '--public-menu-primary': productTheme.primary_color || '#51766E',
      '--public-menu-bg': productTheme.background_color || '#F8FAF9',
      '--public-menu-text': productTheme.text_color || '#333333',
      '--public-menu-surface': productTheme.surface_color || '#FFFFFF',
      '--public-menu-accent': productTheme.accent_color || '#D7E3E0',
      '--public-menu-primary-rgb': hexToRgb(productTheme.primary_color, '81, 118, 110'),
      '--public-menu-bg-rgb': hexToRgb(productTheme.background_color, '248, 250, 249'),
      '--public-menu-text-rgb': hexToRgb(productTheme.text_color, '51, 51, 51'),
      '--public-menu-surface-rgb': hexToRgb(productTheme.surface_color, '255, 255, 255'),
      '--public-menu-accent-rgb': hexToRgb(productTheme.accent_color, '215, 227, 224'),
    }),
    [productTheme],
  );

  const productThemeClassName = [
    'public-product-details',
    `public-product-details--theme-${productTheme.theme || 'light'}`,
    `public-product-details--radius-${productTheme.border_radius || 'large'}`,
    `public-product-details--card-${productTheme.card_style || 'elevated'}`,
    `public-product-details--shadow-${productTheme.card_shadow || 'soft'}`,
    `public-product-details--font-${productTheme.font_family || 'system'}`,
  ].join(' ');
  const cartStorageKey = `${PUBLIC_MENU_CART_PREFIX}${menuStorageId}`;
  const likesStorageKey = `${PUBLIC_MENU_LIKES_PREFIX}${menuStorageId}`;
  const products = useMemo(() => getAllProductsFromMenu(menuData), [menuData]);
  const product = useMemo(() => {
    return products.find((currentProduct) => {
      return (
        String(currentProduct.id) === String(productId) ||
        String(currentProduct.slug || '') === String(productId)
      );

    });
  }, [products, productId]);
  const category = useMemo(() => {
    return findProductCategory(menuData, product);
  }, [menuData, product]);
  const sizes = getProductSizes(product).filter((size) => size.is_active !== false);
  const supplements = getProductSupplements(product).filter(
    (supplement) => supplement.is_active !== false,
  );

  const allergens = getProductAllergens(product);
  const dietaryLabels = getProductDietaryLabels(product);
  const rawProductMedia = getProductMedia(product);
  const productMedia = rawProductMedia.filter(
    (media) => media?.url && !hiddenMediaUrls.includes(media.url),
  );

  const reviews = localReviews;
  const engagement = getEngagement(product);
  const liked = likedProductIds.includes(Number(product?.id));
  const currency = getProductCurrency(product, establishment);
  const available = Boolean(product) && isProductAvailable(product);
  const orderingEnabled = isEnabledFlag(
    menu?.ordering_enabled ??
      menuData?.ordering_enabled ??
      establishment?.ordering_enabled,
    false,
  );

  const canOrder = orderingEnabled && available;
  const productName = getTranslationValue(product, 'name', language);
  const productDescription = getTranslationValue(product, 'description', language);
  const ingredients = getTranslationValue(product, 'ingredients', language);
  const categoryName = getTranslationValue(category, 'name', language);
  const selectedSize = sizes.find(
    (size) => String(size.id) === String(selectedSizeId),
  );

  const activeMedia = productMedia[activeImageIndex] || productMedia[0] || null;
  const activeImage =
    activeMedia?.type === 'image'
      ? activeMedia.url
      : productMedia.find((media) => media.type === 'image')?.url || null;
  const reviewsCount = Math.max(Number(engagement.reviews_count || 0), reviews.length);
  const unitPrice = useMemo(() => {
    if (!product) return 0;
    const basePrice = getProductBasePrice(product, selectedSizeId);
    const addOnsPrice = supplements
      .filter((supplement) => selectedSupplements.includes(String(supplement.id)))
      .reduce((total, supplement) => total + getSupplementPrice(supplement), 0);
    return basePrice + addOnsPrice;
  }, [product, selectedSizeId, selectedSupplements, supplements]);
  const totalPrice = unitPrice * quantity;
  useEffect(() => {
    if (!alertMessage) return undefined;
    const timeoutId = window.setTimeout(() => {
      setAlertMessage(null);
    }, alertMessage.actionTo ? 8000 : 5000);
    return () => {
      window.clearTimeout(timeoutId);
    };

  }, [alertMessage]);
  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const response = await publicMenuApi.getMenu(establishmentSlug, menuSlug);
      setMenuData(response.data.data || null);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }

  };

  useEffect(() => {
    fetchProductDetails();
  }, [establishmentSlug, menuSlug, productId]);
  /*
   * Count the details page as a real product view.
   * Wait until the product is resolved so slug-based product URLs work too.
   */
  useEffect(() => {
    if (!product?.id) return;
    publicMenuApi
      .track(establishmentSlug, menuSlug, {
        type: 'product_view',
        menu_id: menuData?.menu?.id || undefined,
        product_id: Number(product.id),
        category_id: product?.category_id
          ? Number(product.category_id)
          : undefined,
      })
      .catch((error) => {
        console.error('Failed to track product details view:', error);
      });
  }, [establishmentSlug, menuSlug, product?.id, product?.category_id, menuData?.menu?.id]);
  useEffect(() => {
    try {
      const savedLikes = localStorage.getItem(likesStorageKey);
      setLikedProductIds(savedLikes ? JSON.parse(savedLikes) : []);
    } catch {
      setLikedProductIds([]);
    }

  }, [likesStorageKey]);
  useEffect(() => {
    localStorage.setItem(likesStorageKey, JSON.stringify(likedProductIds));
  }, [likedProductIds, likesStorageKey]);
  useEffect(() => {
    setLocalReviews(getReviews(product));
    setHiddenMediaUrls([]);
    setActiveImageIndex(0);
    setSelectedSupplements([]);
    setQuantity(1);
    setItemNote('');
    setAlertMessage(null);
    setFieldErrors({});
    setReviewForm({
      customer_name: '',
      rating: 5,
      comment: '',
    });
    if (!sizes.length) {
      setSelectedSizeId('');
      return;
    }

    const defaultSize = sizes.find((size) => size.is_default) || sizes[0];
    setSelectedSizeId(defaultSize?.id ? String(defaultSize.id) : '');
  }, [product?.id]);
  useEffect(() => {
    if (activeImageIndex > Math.max(productMedia.length - 1, 0)) {
      setActiveImageIndex(0);
    }

  }, [activeImageIndex, productMedia.length]);
  const hideMediaUrl = (mediaUrl) => {
    if (!mediaUrl) return;
    setHiddenMediaUrls((currentUrls) => {
      if (currentUrls.includes(mediaUrl)) return currentUrls;
      return [...currentUrls, mediaUrl];
    });
  };

  const goToPreviousImage = () => {
    if (productMedia.length <= 1) return;
    setActiveImageIndex((currentIndex) =>
      currentIndex === 0 ? productMedia.length - 1 : currentIndex - 1,
    );

  };

  const goToNextImage = () => {
    if (productMedia.length <= 1) return;
    setActiveImageIndex((currentIndex) =>
      currentIndex === productMedia.length - 1 ? 0 : currentIndex + 1,
    );

  };

  const toggleSupplement = (supplementId) => {
    const stringId = String(supplementId);
    setSelectedSupplements((currentIds) =>
      currentIds.includes(stringId)
        ? currentIds.filter((id) => id !== stringId)
        : [...currentIds, stringId],
    );

  };

  const updateProductInMenu = (productIdToUpdate, updater) => {
    setMenuData((currentData) => {
      if (!currentData) return currentData;
      const updateProduct = (currentProduct) => {
        if (Number(currentProduct?.id) !== Number(productIdToUpdate)) {
          return currentProduct;
        }

        return updater(currentProduct);
      };

      const updateCategory = (categoryItem) => ({
        ...categoryItem,
        products: toArray(categoryItem.products).map(updateProduct),
        children: toArray(categoryItem.children).map((childCategory) => ({
          ...childCategory,
          products: toArray(childCategory.products).map(updateProduct),
        })),
      });
      return {
        ...currentData,
        products: toArray(currentData.products).map(updateProduct),
        featured_products: toArray(currentData.featured_products).map(updateProduct),
        categories: toArray(currentData.categories).map(updateCategory),
      };

    });
  };

  const toggleLike = async () => {
    if (!product?.id) return;
    const alreadyLiked = likedProductIds.includes(Number(product.id));
    setLikedProductIds((currentIds) =>
      alreadyLiked
        ? currentIds.filter((id) => Number(id) !== Number(product.id))
        : [...currentIds, Number(product.id)],
    );

    updateProductInMenu(product.id, (currentProduct) => {
      const currentEngagement = getEngagement(currentProduct);
      return {
        ...currentProduct,
        engagement: {
          ...currentEngagement,
          likes_count: Math.max(
            0,
            Number(currentEngagement.likes_count || 0) + (alreadyLiked ? -1 : 1),
          ),
        },
      };

    });
    try {
      await publicMenuApi.toggleLike(establishmentSlug, menuSlug, product.id);
    } catch {
      // Keep optimistic like update.
    }

  };

  const submitReview = async (event) => {
    event.preventDefault();
    if (!product?.id || submittingReview) return;
    const customerName = reviewForm.customer_name.trim();
    const comment = reviewForm.comment.trim();
    const nextFieldErrors = {};
    if (!customerName) {
      nextFieldErrors.customer_name = t('publicMenu.productModal.reviewNameRequired');
    }

    if (!comment) {
      nextFieldErrors.comment = t('publicMenu.productModal.reviewCommentRequired');
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      triggerErrorShake();
      setFieldErrors(nextFieldErrors);
      setAlertMessage({
        type: 'error',
        text: t('publicMenu.productModal.formRequiredAlert'),
      });
      return;
    }

    setFieldErrors({});
    try {
      setSubmittingReview(true);
      await publicMenuApi.createReview(product.id, {
        customer_name: customerName,
        rating: Number(reviewForm.rating || 5),
        comment,
      });
      const nextReview = {
        id: `local-${Date.now()}`,
        customer_name: customerName,
        rating: Number(reviewForm.rating || 5),
        comment,
        created_at: new Date().toISOString(),
        is_local: true,
      };

      setLocalReviews((currentReviews) => [nextReview, ...currentReviews]);
      updateProductInMenu(product.id, (currentProduct) => {
        const currentEngagement = getEngagement(currentProduct);
        return {
          ...currentProduct,
          engagement: {
            ...currentEngagement,
            reviews_count: Number(currentEngagement.reviews_count || 0) + 1,
          },
        };

      });
      setReviewForm({
        customer_name: '',
        rating: 5,
        comment: '',
      });
      setAlertMessage({
        type: 'success',
        text: t('publicMenu.productModal.reviewAdded'),
      });
    } catch {
      triggerErrorShake();
      setAlertMessage({
        type: 'error',
        text: t('publicMenu.productModal.reviewError'),
      });
    } finally {
      setSubmittingReview(false);
    }

  };

  const buildCartItem = () => {
    const selectedSupplementsData = supplements.filter((supplement) =>
      selectedSupplements.includes(String(supplement.id)),
    );

    return {
      row_id: `${product.id}-${Date.now()}-${Math.random()}`,
      product_id: product.id,
      product_size_id: selectedSizeId || null,
      supplement_ids: selectedSupplementsData.map((supplement) => supplement.id),
      quantity,
      note: itemNote.trim() || null,
      unit_price: unitPrice,
      total_price: totalPrice,
      product_snapshot: {
        name: productName,
        image: activeImage,
        currency,
        size_name: selectedSize
          ? getTranslationValue(selectedSize, 'name', language)
          : '',
        supplements: selectedSupplementsData.map((supplement) => ({
          id: supplement.id,
          name: getTranslationValue(supplement, 'name', language),
          price: getSupplementPrice(supplement),
        })),
      },
    };

  };

  const addToCart = ({ goToCheckout = false } = {}) => {
    if (!canOrder || !product) return;
    if (product?.pricing_type === 'sizes' && sizes.length > 0 && !selectedSizeId) {
      triggerErrorShake();
      setAlertMessage({
        type: 'error',
        text: t('publicMenu.productModal.selectSizeAlert'),
      });
      return;
    }

    const cartItem = buildCartItem();
    const storedItems = readCartItems(cartStorageKey);
    const nextItems = [...storedItems, cartItem];
    saveCartItems(cartStorageKey, nextItems);
    setAlertMessage({
      type: 'success',
      text: t('publicMenu.productModal.addedToCart'),
      actionTo: `${publicMenuPath}/order`,
      actionLabel: t('publicMenu.cart.proceedToCheckout'),
    });
    if (goToCheckout) {
      navigate(`${publicMenuPath}/order`, {
        state: {
          cartUpdatedAt: Date.now(),
        },
      });
    }

  };

  const orderNow = () => {
    addToCart({ goToCheckout: true });
  };

  if (loading) {
    return (
      <main className={`${productThemeClassName} public-product-details--loading`} style={productThemeStyle}>
        <Loader2 size={38} />
        <h1>{t('productDetails.loading')}</h1>
      </main>
    );

  }

  if (loadError || !product) {
    return (
      <main className={`${productThemeClassName} public-product-details--loading`} style={productThemeStyle}>
        <UtensilsCrossed size={42} />
        <h1>{t('productDetails.notFound')}</h1>
        <Link to={publicMenuPath}>
          <ArrowLeft size={18} />
          {t('productDetails.backToMenu')}
        </Link>
      </main>
    );

  }

  return (
    <main className={productThemeClassName} style={productThemeStyle}>
      <section className="product-details-shell">
        <section className="product-details-hero">
          {/* Gallery */}
          <div className="product-details-gallery">
            <div className="product-details-media-stage">
              {activeMedia?.type === 'video' ? (
                <video
                  key={activeMedia.url}
                  className="product-details-media-file"
                  src={activeMedia.url}
                  poster={activeMedia.thumbnail || undefined}
                  controls
                  playsInline
                />
              ) : activeMedia?.url ? (
                <img
                  className="product-details-media-file"
                  src={activeMedia.url}
                  alt={productName}
                  onError={() => hideMediaUrl(activeMedia.url)}
                />
              ) : (
                <div className="product-details-media-empty">
                  <ImageIcon size={46} />
                </div>
              )}
              <Link
                to={publicMenuPath}
                className="product-details-fab product-details-fab--back"
                aria-label={t('productDetails.backToMenu')}
              >
                <ArrowLeft size={19} />
                <span className="product-details-fab__label">
                  {t('productDetails.backToMenu')}
                </span>
              </Link>
              <button
                type="button"
                className={`product-details-fab product-details-fab--like ${liked ? 'is-liked' : ''}`}
                onClick={toggleLike}
                aria-pressed={liked}
                aria-label="Like"
              >
                <Heart size={19} fill={liked ? 'currentColor' : 'none'} />
              </button>
              {activeMedia?.type === 'video' && (
                <span className="product-details-media-badge">
                  <PlayCircle size={15} />
                  Video
                </span>
              )}
              {productMedia.length > 1 && (
                <>
                  <button
                    type="button"
                    className="product-details-gallery-arrow product-details-gallery-arrow--left"
                    onClick={goToPreviousImage}
                    aria-label="Previous"
                  >
                    <ChevronLeft size={22} />
                  </button>
                  <button
                    type="button"
                    className="product-details-gallery-arrow product-details-gallery-arrow--right"
                    onClick={goToNextImage}
                    aria-label="Next"
                  >
                    <ChevronRight size={22} />
                  </button>
                  <span className="product-details-image-count">
                    {activeImageIndex + 1}/{productMedia.length}
                  </span>
                </>
              )}
            </div>
          </div>
          {/* Summary + options */}
          <aside className="product-details-summary">
            {dietaryLabels.length > 0 && (
              <div className="product-details-badges">
                {dietaryLabels.slice(0, 3).map((label) => (
                  <span key={label.id || getTranslationValue(label, 'name', language)}>
                    {getTranslationValue(label, 'name', language) || label.name}
                  </span>
                ))}
              </div>
            )}
            <div className="product-details-title-row">
              <div className="product-details-title-block">
                <h1>{productName}</h1>
                {categoryName && (
                  <span className="product-details-category">{categoryName}</span>
                )}
              </div>
              <strong className="product-details-title-price">
                {getProductPrice(product, t, establishment)}
              </strong>
            </div>
            <div className="product-details-engagement">
              <span>
                <Star size={15} className="product-details-star" fill="currentColor" />
                {Number(engagement.rating_average || 0).toFixed(1)}
              </span>
              <span className={liked ? 'is-liked' : ''}>
                <Heart size={15} fill={liked ? 'currentColor' : 'none'} />
                {engagement.likes_count || 0}
              </span>
              <span>
                <MessageCircle size={15} />
                {reviewsCount}
              </span>
              <span className={available ? 'is-available' : 'is-unavailable'}>
                {available
                  ? t('publicMenu.productModal.available')
                  : t('publicMenu.productModal.unavailable')}
              </span>
              {!orderingEnabled && (
                <span className="is-ordering-off">
                  {t('publicMenu.productModal.orderingDisabled')}
                </span>
              )}
            </div>
            {productDescription && (
              <p className="product-details-description">{productDescription}</p>
            )}
            {product.discount_price && (
              <div className="product-details-price">
                <span>{t('productDetails.discount')}</span>
              </div>
            )}
            {sizes.length > 0 && (
              <section className="product-details-section">
                <h2 className="product-details-section-head">
                  {t('publicMenu.productModal.sizes')}
                </h2>
                <div className="product-details-size-row">
                  {sizes.map((size) => {
                    const active = String(size.id) === String(selectedSizeId);
                    return (
                      <button
                        key={size.id}
                        type="button"
                        className={`product-details-size ${active ? 'active' : ''}`}
                        aria-pressed={active}
                        onClick={() => setSelectedSizeId(String(size.id))}
                      >
                        <span>{getTranslationValue(size, 'name', language)}</span>
                        <small>{formatPrice(size.discount_price ?? size.price, currency)}</small>
                      </button>
                    );

                  })}
                </div>
              </section>
            )}
            {supplements.length > 0 && (
              <section className="product-details-section">
                <h2 className="product-details-section-head">
                  {t('publicMenu.productModal.addons')}
                </h2>
                <div className="product-details-extras">
                  {supplements.map((supplement) => {
                    const active = selectedSupplements.includes(String(supplement.id));
                    return (
                      <button
                        key={supplement.id}
                        type="button"
                        role="checkbox"
                        aria-checked={active}
                        className={`product-details-extra ${active ? 'active' : ''}`}
                        onClick={() => toggleSupplement(supplement.id)}
                      >
                        <span className="product-details-extra__box">
                          {active && <Check size={14} strokeWidth={3} />}
                        </span>
                        <span className="product-details-extra__name">
                          {getTranslationValue(supplement, 'name', language)}
                        </span>
                        <strong>+ {formatPrice(getSupplementPrice(supplement), currency)}</strong>
                      </button>
                    );

                  })}
                </div>
              </section>
            )}
            {canOrder ? (
              <label className="product-details-note">
                <Pencil size={16} />
                <input
                  value={itemNote}
                  placeholder={t('publicMenu.productModal.itemNote')}
                  onChange={(event) => setItemNote(event.target.value)}
                />
              </label>
            ) : (
              <div className="product-details-order-disabled-note">
                <AlertTriangle size={17} />
                <span>
                  {!orderingEnabled
                    ? t('publicMenu.productModal.orderingDisabledHint')
                    : t('publicMenu.productModal.unavailable')}
                </span>
              </div>
            )}
          </aside>
        </section>
        {/* Full information */}
        <section className="product-details-content-grid">
          <article className="product-details-info-card">
            <h2>{t('productDetails.fullInfo')}</h2>
            <div className="product-details-info-list">
              <div>
                <span>{t('productDetails.status')}</span>
                <strong>
                  {available
                    ? t('publicMenu.productModal.available')
                    : t('publicMenu.productModal.unavailable')}
                </strong>
              </div>
              <div>
                <span>{t('productDetails.category')}</span>
                <strong>{categoryName || '-'}</strong>
              </div>
              <div>
                <span>{t('productDetails.price')}</span>
                <strong>{getProductPrice(product, t, establishment)}</strong>
              </div>
              <div>
                <span>{t('productDetails.productCode')}</span>
                <strong>{product.sku || product.code || product.slug || product.id}</strong>
              </div>
            </div>
            {ingredients && (
              <div className="product-details-text-block">
                <h3>{t('publicMenu.productModal.ingredients')}</h3>
                <p>{ingredients}</p>
              </div>
            )}
            {allergens.length > 0 && (
              <div className="product-details-text-block">
                <h3>{t('publicMenu.productModal.allergens')}</h3>
                <div className="product-details-tags">
                  {allergens.map((allergen) => (
                    <span key={allergen.id || getTranslationValue(allergen, 'name', language)}>
                      {getTranslationValue(allergen, 'name', language) || allergen.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {dietaryLabels.length > 0 && (
              <div className="product-details-text-block">
                <h3>{t('publicMenu.productModal.dietaryLabels')}</h3>
                <div className="product-details-tags">
                  {dietaryLabels.map((label) => (
                    <span key={label.id || getTranslationValue(label, 'name', language)}>
                      {getTranslationValue(label, 'name', language) || label.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </article>
        </section>
        {/* Reviews */}
        <section className="product-details-reviews">
          <div className="product-details-section-title">
            <MessageCircle size={20} />
            <h2>{t('publicMenu.productModal.reviews')}</h2>
          </div>
          <form className="product-details-review-form" onSubmit={submitReview}>
            <div className="product-details-review-form__grid">
              <label>
                <span>{t('publicMenu.productModal.namePlaceholder')}</span>
                <input
                  value={reviewForm.customer_name}
                  placeholder={t('publicMenu.productModal.namePlaceholder')}
                  className={fieldErrors.customer_name ? 'is-invalid' : ''}
                  onChange={(event) => {
                    setReviewForm((current) => ({
                      ...current,
                      customer_name: event.target.value,
                    }));
                    setFieldErrors((current) => ({
                      ...current,
                      customer_name: '',
                    }));
                  }}
                />
                {fieldErrors.customer_name && (
                  <small className="product-details-field-error">
                    {fieldErrors.customer_name}
                  </small>
                )}
              </label>
              <div className="product-details-review-rating">
                <span>{t('publicMenu.productModal.leaveReview')}</span>
                <div>
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      className={reviewForm.rating >= rating ? 'active' : ''}
                      onClick={() =>
                        setReviewForm((current) => ({
                          ...current,
                          rating,
                        }))
                      }

                    >
                      <Star size={18} fill="currentColor" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <label>
              <span>{t('publicMenu.productModal.commentPlaceholder')}</span>
              <textarea
                value={reviewForm.comment}
                placeholder={t('publicMenu.productModal.commentPlaceholder')}
                className={fieldErrors.comment ? 'is-invalid' : ''}
                onChange={(event) => {
                  setReviewForm((current) => ({
                    ...current,
                    comment: event.target.value,
                  }));
                  setFieldErrors((current) => ({
                    ...current,
                    comment: '',
                  }));
                }}
              />
              {fieldErrors.comment && (
                <small className="product-details-field-error">
                  {fieldErrors.comment}
                </small>
              )}
            </label>
            <button type="submit" disabled={submittingReview}>
              {submittingReview ? <Loader2 size={17} /> : <Send size={17} />}
              {t('publicMenu.productModal.sendReview')}
            </button>
          </form>
          {reviews.length > 0 ? (
            <div className="product-details-review-grid">
              {reviews.map((review, index) => (
                <article key={review.id || index}>
                  <div>
                    <strong>{review.customer_name || t('publicMenu.anonymous')}</strong>
                    <span>
                      <Star size={14} fill="currentColor" />
                      {review.rating || 5}
                    </span>
                  </div>
                  {review.comment && <p>{review.comment}</p>}
                  {review.is_local && (
                    <small>{t('publicMenu.productModal.justAdded')}</small>
                  )}
                </article>
              ))}
            </div>
          ) : (
            <div className="product-details-empty product-details-empty--large">
              <MessageCircle size={28} />
              {t('publicMenu.productModal.noReviews')}
            </div>
          )}
        </section>
        {/* Sticky purchase bar */}
        {canOrder && (
          <div className="product-details-dock">
            <div className="product-details-dock__quantity">
              <button
                type="button"
                aria-label="Decrease quantity"
                onClick={() => setQuantity((current) => Math.max(1, current - 1))}
              >
                <Minus size={16} />
              </button>
              <strong>{quantity}</strong>
              <button
                type="button"
                aria-label="Increase quantity"
                onClick={() => setQuantity((current) => current + 1)}
              >
                <Plus size={16} />
              </button>
            </div>
            <button
              type="button"
              className="product-details-dock__secondary"
              onClick={orderNow}
            >
              {t('publicMenu.productModal.orderNow')}
            </button>
            <button
              type="button"
              className="product-details-dock__primary"
              onClick={() => addToCart()}
            >
              <span>
                <ShoppingBag size={18} />
                {t('publicMenu.productModal.addToCart')}
              </span>
              <strong>{formatPrice(totalPrice, currency)}</strong>
            </button>
          </div>
        )}
      </section>
      <ProductDetailsCustomAlert
        alertMessage={alertMessage}
        closeLabel={t('common.close')}
        onClose={() => setAlertMessage(null)}
      />
    </main>
  );

}

export default PublicProductDetails;
